import { Button, Card, List, Stack, Text, Title } from '@mantine/core';
import { IconCreditCard } from '@tabler/icons-react';

/**
 * Renders the "subscribe to Storefront" upsell shown when a shop's gate is
 * lapsed (`writesAllowed === false`).
 *
 * Shell: (desktop). Used by {@link SubscriptionGate} as the fallback when the
 * caller's shop is unsubscribed; not rendered directly. The Subscribe CTA
 * href is a placeholder until the Stripe checkout Edge Function ships
 * (Epic 0 follow-up). When checkout is ready, the href becomes the
 * `create-checkout-session` endpoint URL.
 *
 * Non-obvious contracts:
 *   - The status string is only displayed for staff diagnostics — copy
 *     deliberately stays neutral so owners aren't blamed for a shop's
 *     `past_due` state that they're already getting Stripe emails about.
 */
export interface UpsellCardProps {
  /** The shop's tier id — surfaced in the help text. */
  tierId?: string;
  /**
   * The shop's current subscription_status — surfaced in the help text so
   * staff can debug "wait, why am I seeing the upsell?" without opening psql.
   */
  status?: string;
}

export function UpsellCard({ tierId, status }: UpsellCardProps) {
  return (
    <Card withBorder padding="xl" radius="md" maw={620} mx="auto" mt="xl">
      <Stack gap="md">
        <Title order={2}>Subscribe to VendorPlus / Storefront</Title>
        <Text c="dimmed">
          VendorPlus / Storefront ($249.99/mo) unlocks inventory, label printing, kiosk browse, and
          the rest of the shop tools. Your data is preserved while you decide — re-subscribe at any
          time and pick up where you left off.
        </Text>

        <List size="sm" spacing={4}>
          <List.Item>Bins, binders, slab binders, display cases, sealed stock</List.Item>
          <List.Item>Avery / Dymo / Brother / round-1″ label printing</List.Item>
          <List.Item>Multi-location stock, transfers, audit sessions</List.Item>
          <List.Item>Consignment intake + payout periods</List.Item>
        </List>

        <Button
          component="a"
          href="#subscribe"
          leftSection={<IconCreditCard size={18} stroke={1.5} />}
          size="md"
        >
          Subscribe to VendorPlus / Storefront — $249.99/mo
        </Button>

        {(tierId !== undefined || status !== undefined) && (
          <Text size="xs" c="dimmed">
            Current state: tier <code>{tierId ?? 'unknown'}</code>, status{' '}
            <code>{status ?? 'unknown'}</code>. Contact{' '}
            <a href="mailto:support@mytcgbinder.com">support@mytcgbinder.com</a> if this looks
            wrong.
          </Text>
        )}
      </Stack>
    </Card>
  );
}
